import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Copy } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

import {
  useGetShop,
  useUpdateShopSettings,
  useDisableMixpanel,
  getGetShopQueryKey,
} from '@/api/generated/v2-shop/v2-shop'
import {
  useGetScriptTagStatus,
} from '@/api/generated/v2-scripttag/v2-scripttag'
import type {
  ShopResponse as OrvalShopResponse,
  ScriptTagStatusResponse,
  ShopSettingsUpdateResponse,
} from '@/api/schemas'
import { HistoricalImportStatus } from '@/features/setup/HistoricalImportStatus'
import { SetupPage } from '@/features/setup/SetupPage'
import { TotalValueBoardCard } from '@/features/setup/TotalValueBoardCard'
import { GuideStep } from '@/components/guide/GuideStep'
import { GuideImage } from '@/components/guide/GuideImage'
import { TipBox } from '@/components/guide/TipBox'
import { Input } from '@/components/ui/Input'
import { MaskedField } from '@/components/ui/MaskedField'
import { SaveErrorBanner } from '@/components/ui/SaveErrorBanner'
import { SettingsSection } from '@/components/ui/SettingsSection'
import { auth } from '@/lib/auth'
import { useConfirm } from '@/hooks/useConfirm'

export const Route = createFileRoute('/_dashboard/setup/mixpanel')({
  component: MixpanelSetupPage,
})

/** Mixpanel 폼 필드 타입 */
type MixpanelFormData = {
  mixpanel_token: string
  mixpanel_project_id: string
  mixpanel_sa_username: string
  mixpanel_sa_secret: string
  mixpanel_session_replay_percent: number
  mixpanel_web_vitals_sample_percent: number
}

/**
 * 백엔드 검증 실패 시 어느 입력 필드를 강조(빨간 테두리)할지 결정한다.
 * 백엔드가 내려주는 validation_error + error_code 조합으로 매핑한다.
 */
function fieldsToHighlight(
  validationError?: string,
  errorCode?: string,
): (keyof MixpanelFormData)[] {
  if (validationError === 'mixpanel_token') return ['mixpanel_token']
  // 다른 샵이 이미 쓰는 프로젝트 ID(중복 연결 차단)
  if (validationError === 'mixpanel_project_id') return ['mixpanel_project_id']
  // 프로젝트 ID가 틀렸을 가능성
  if (errorCode === 'sa_project_not_found') return ['mixpanel_project_id']
  // 서비스 계정이 이 프로젝트에 연결 안 됨 → 프로젝트 ID + SA 둘 다 의심
  if (errorCode === 'sa_project')
    return ['mixpanel_project_id', 'mixpanel_sa_username', 'mixpanel_sa_secret']
  // 쓰기 권한 부족(Role≠Admin) → 입력값 문제가 아니라 Mixpanel Role 문제라
  // 필드 강조 없이 배너 안내만 (고칠 곳이 입력란이 아님)
  if (errorCode === 'sa_no_write') return []
  // 사용자명/시크릿 자체가 틀림
  if (errorCode === 'sa_auth' || validationError === 'mixpanel_sa')
    return ['mixpanel_sa_username', 'mixpanel_sa_secret']
  return []
}

/**
 * Shop 데이터로부터 폼 초기값을 만든다
 * — secret 은 응답에 평문이 없으므로 항상 빈 값으로 시작
 */
function buildFormDefaults(shop: OrvalShopResponse | null): MixpanelFormData {
  return {
    mixpanel_token: shop?.mixpanel_token || '',
    mixpanel_project_id: shop?.mixpanel_project_id || '',
    mixpanel_sa_username: shop?.mixpanel_sa_username || '',
    mixpanel_sa_secret: '',
    mixpanel_session_replay_percent: shop?.mixpanel_session_replay_percent ?? 30,
    // 웹 성능(Core Web Vitals) 수집 비율 — 미설정 시 기본 30%
    mixpanel_web_vitals_sample_percent: shop?.mixpanel_web_vitals_sample_percent ?? 30,
  }
}

function MixpanelSetupPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Orval 훅이 중간 saving 상태를 관리 못하므로 별도 state 유지
  const [saving, setSaving] = useState(false)
  // View/Edit 모드 — 미연동 상태면 처음부터 edit 모드로 진입
  const [isEditing, setIsEditing] = useState(false)
  // 저장 실패 사유 — toast와 별개로 폼에 계속 떠 있는 영구 배너용
  const [saveError, setSaveError] = useState<string | null>(null)
  const { confirm, ConfirmDialog } = useConfirm()

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm<MixpanelFormData>({
    defaultValues: buildFormDefaults(null),
  })

  // --- Orval Query 훅: Shop 정보 조회 ---
  const { data: shopRaw, isLoading: shopLoading } = useGetShop({
    query: { enabled: auth.isAuthenticated() },
  })
  const shop = (shopRaw ?? null) as OrvalShopResponse | null

  // --- Orval Query 훅: 스크립트 설치 상태 조회 ---
  const { data: scriptRaw } = useGetScriptTagStatus({
    query: { enabled: auth.isAuthenticated() },
  })
  const scriptStatus = (scriptRaw ?? null) as ScriptTagStatusResponse | null
  const scriptInstalled = scriptStatus?.installed ?? null

  // --- Orval Mutation 훅: 설정 업데이트 ---
  const updateSettingsMutation = useUpdateShopSettings()
  const disableMixpanelMutation = useDisableMixpanel()

  // 인증 안 된 경우 리다이렉트
  useEffect(() => {
    if (!auth.isAuthenticated()) {
      navigate({ to: '/' })
    }
  }, [navigate])

  // Shop 데이터 로드 완료 시 폼 초기값 설정
  // edit 중에는 외부 리페치로 사용자 입력을 덮어쓰지 않도록 가드
  useEffect(() => {
    if (!shop) return
    if (isEditing) return
    reset(buildFormDefaults(shop))
  }, [shop, isEditing, reset])

  // 미연동 상태로 처음 로드되면 edit 모드로 진입
  useEffect(() => {
    if (!shop) return
    if (!shop.mixpanel_token && !isEditing) {
      setIsEditing(true)
    }
    // 의도적으로 isEditing 의존성 제외 — 최초 1회만 동작
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop])

  const isConnected = !!shop?.mixpanel_token

  /** 설정 저장.
   *
   * `confirmShared` 는 "이미 다른 샵이 쓰는 프로젝트에 그대로 합치겠다"는 사용자
   * 동의다. react-hook-form 의 handleSubmit 은 (data, event) 로 호출하므로,
   * 이 인자를 onSubmit 시그니처에 두면 event 가 truthy 로 새어들어온다.
   * 그래서 제출 핸들러(onSubmit)와 실제 저장(submitSettings)을 분리한다.
   */
  const submitSettings = async (
    data: MixpanelFormData,
    confirmShared: boolean,
  ) => {
    if (!shop) return
    setSaving(true)
    // 이전 실패 흔적 초기화 (배너 + 필드 강조)
    setSaveError(null)
    clearErrors()

    try {
      // Orval ShopSettingsUpdateRequest에 맞는 payload 구성
      const updateData: Record<string, unknown> = {
        mixpanel_token: data.mixpanel_token,
        mixpanel_project_id: data.mixpanel_project_id || null,
        mixpanel_session_replay_percent: data.mixpanel_session_replay_percent,
        mixpanel_web_vitals_sample_percent: data.mixpanel_web_vitals_sample_percent,
      }

      if (data.mixpanel_sa_username) {
        updateData.mixpanel_sa_username = data.mixpanel_sa_username
      }
      if (data.mixpanel_sa_secret) {
        updateData.mixpanel_sa_secret = data.mixpanel_sa_secret
      }
      if (confirmShared) {
        updateData.confirm_shared_project = true
      }

      const result = await updateSettingsMutation.mutateAsync({
        data: updateData as any,
      })
      const response = result as unknown as ShopSettingsUpdateResponse
      // validation_error / error_code / error_detail / integration_status 는
      // 모두 response.data 안에 들어있다 (백엔드 ShopSettingsUpdateResponse.data).
      const resData = (response.data ?? {}) as Record<string, any>

      if (response.success) {
        setSaveError(null)
        if (resData.integration_status?.mixpanel?.is_valid) {
          // SA 검증 결과엔 project_name 이 없으므로(토큰 경로일 때만 존재),
          // 사용자가 입력한 프로젝트 ID로 안내한다.
          toast.success(
            data.mixpanel_project_id
              ? `믹스패널 연동 성공! (프로젝트 ID: ${data.mixpanel_project_id})`
              : '믹스패널 연동 성공!',
            { duration: 5000 },
          )
        } else {
          toast.success('설정이 저장되었어요.')
        }
        // Shop 쿼리 무효화 → 자동 리페치
        await queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
        // 성공 시 view 모드로 복귀
        setIsEditing(false)
      } else if (resData.requires_confirmation && !confirmShared) {
        // 실패가 아니라 "되묻기" — 같은 소유자의 다른 샵이 이미 쓰는 프로젝트다.
        // 에러 배너 대신 확인을 받고, 동의하면 플래그를 실어 재요청한다.
        const agreed = await confirm(
          resData.error_detail ?? '다른 샵과 같은 프로젝트를 쓰게 돼요.',
          {
            title: '다른 샵과 프로젝트를 함께 쓸까요?',
            confirmText: '그대로 저장',
            cancelText: '취소',
          },
        )
        if (agreed) {
          await submitSettings(data, true)
        } else {
          // 취소했는데 아무 표시가 없으면 저장된 줄 안다
          setSaveError(
            '저장하지 않았어요. 다른 샵과 프로젝트를 함께 쓰려면 다시 저장하고 확인해 주세요.',
          )
        }
      } else {
        // 저장 실패 — 영구 배너에 상세 사유(깨끗한 error_detail), 짧은 toast로
        // 즉시 인지, error_code로 문제 필드에 빨간 테두리.
        const detail =
          resData.error_detail ||
          response.message ||
          '입력값을 확인하고 다시 시도해 주세요.'
        setSaveError(detail)
        toast.error('저장에 실패했어요. 입력값을 확인해 주세요.')
        const badFields = fieldsToHighlight(
          resData.validation_error,
          resData.error_code,
        )
        badFields.forEach((f) =>
          setError(f, { type: 'server', message: '확인이 필요해요' }),
        )
        // 긴 폼에서도 문제 위치가 보이도록 첫 번째 문제 필드로 스크롤/포커스
        if (badFields.length > 0) setFocus(badFields[0])
      }
    } catch (error: any) {
      const detail =
        error?.message || '저장 중 문제가 생겼어요. 잠시 후 다시 시도해 주세요.'
      setSaveError(detail)
      toast.error('저장에 실패했어요.')
    } finally {
      setSaving(false)
    }
  }

  /** react-hook-form 제출 핸들러 — 최초 저장은 항상 미확인 상태로 보낸다 */
  const onSubmit = (data: MixpanelFormData) => submitSettings(data, false)

  /** 연동 해제 */
  const handleDisconnect = async () => {
    if (!shop) return
    const ok = await confirm(
      '방문자 데이터 수집이 멈추고, 입력한 토큰·서비스 계정 정보가 모두 지워져요.',
      { title: '믹스패널 연동을 해제할까요?', confirmText: '연동 해제', variant: 'danger' },
    )
    if (!ok) return

    setSaving(true)
    try {
      // 전용 해제 엔드포인트 사용 — 모든 credential 명시적 clear
      await disableMixpanelMutation.mutateAsync()
      toast.success('연동을 해제했어요')
      await queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
      reset({
        mixpanel_token: '',
        mixpanel_project_id: '',
        mixpanel_sa_username: '',
        mixpanel_sa_secret: '',
        mixpanel_session_replay_percent: 30,
        mixpanel_web_vitals_sample_percent: 30,
      })
      setIsEditing(true)
    } catch (err: any) {
      // 백엔드에서 의존성 검증 실패 시 명시적 메시지 노출
      const msg =
        err?.message ||
        '연동을 해제하지 못했어요. 잠시 후 다시 시도해 주세요'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  /** 설정 수정 시작 — 폼 reset + 첫 필드 focus */
  const handleEdit = () => {
    reset(buildFormDefaults(shop))
    setSaveError(null)
    setIsEditing(true)
    // 다음 렌더 이후 focus
    setTimeout(() => setFocus('mixpanel_token'), 0)
  }

  /** 수정 취소 — 폼 원복 + view 모드 복귀 */
  const handleCancel = () => {
    reset(buildFormDefaults(shop))
    setSaveError(null)
    setIsEditing(false)
  }

  if (shopLoading || !shop) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-outline-variant text-sm">로딩 중...</div>
      </div>
    )
  }

  const externalLink = shop.mixpanel_project_id
    ? `https://mixpanel.com/project/${shop.mixpanel_project_id}`
    : undefined

  // 선행 조건: 카페24 스크립트가 설치되어 있어야 방문자 분석을 켤 수 있다.
  // (믹스패널은 트래커 스크립트가 페이지에서 이벤트를 보내야 동작하므로)
  // 이미 연동된 상태에서 스크립트가 (일시적으로) 미설치로 잡히면 안내만 띄우고 편집은 막지 않는다.
  const scriptMissing = scriptInstalled === false
  const prerequisiteMessage = scriptMissing
    ? '카페24 스크립트를 먼저 설치해주세요. 스크립트가 설치되어야 방문자 데이터를 수집할 수 있어요.'
    : undefined
  // 미연동 상태에서 스크립트가 없으면 저장 자체를 막는다.
  const blockedByScript = scriptMissing && !isConnected

  // ===== View 모드 콘텐츠 =====
  const viewContent = (
    <div className="space-y-4">
      {/* 연동 직후 자동으로 도는 과거 1년치 소급 적재 진행 상황.
          수 시간 걸리는 작업이라 화면이 침묵하면 "연동했는데 왜 과거가 없지"가 된다. */}
      <HistoricalImportStatus enabled={isConnected} />
      <MaskedField
        label="프로젝트 토큰 (Project Token)"
        value={shop.mixpanel_token}
        mode="token"
      />
      <MaskedField
        label="프로젝트 ID (Project ID)"
        value={shop.mixpanel_project_id}
        mode="plain"
      />

      <div className="pt-3 border-t border-border space-y-3">
        <p className="text-xs text-on-surface-variant font-medium">
          서비스 계정 (Service Account) 인증
        </p>
        <MaskedField
          label="서비스 계정 사용자명 (Username)"
          value={shop.mixpanel_sa_username}
          mode="token"
        />
        <MaskedField
          label="서비스 계정 시크릿 (Secret)"
          value={null}
          mode="secret"
          isSet={shop.mixpanel_sa_secret_set}
        />
      </div>

      <div className="pt-3 border-t border-border">
        <p className="text-xs text-on-surface-variant font-medium mb-1">
          세션 리플레이
        </p>
        <p className="text-xs text-outline-variant mb-3">
          방문자 화면을 녹화하여 행동을 분석해요.
        </p>
        <MaskedField
          label="녹화 비율 (%)"
          value={String(shop.mixpanel_session_replay_percent ?? 30)}
          mode="plain"
          copyable={false}
        />
      </div>

      <div className="pt-3 border-t border-border">
        <p className="text-xs text-on-surface-variant font-medium mb-1">
          웹 성능 측정
        </p>
        <p className="text-xs text-outline-variant mb-3">
          페이지 로딩 속도(Core Web Vitals)를 측정해 수집해요.
        </p>
        <MaskedField
          label="웹 성능 수집 비율 (%)"
          value={String(shop.mixpanel_web_vitals_sample_percent ?? 30)}
          mode="plain"
          copyable={false}
        />
      </div>
    </div>
  )

  // ===== Edit 모드 콘텐츠 (기존 폼 그대로) =====
  const editContent = (
    <div className="space-y-4">
      {/* 저장 실패 시 사라지지 않는 배너 — 무엇을 고쳐야 하는지 계속 안내 */}
      <SaveErrorBanner message={saveError} />
      <Input
        label="프로젝트 토큰 (Project Token)"
        placeholder="Access Keys에서 복사한 토큰"
        {...register('mixpanel_token', { required: '프로젝트 토큰을 입력해주세요.' })}
        error={errors.mixpanel_token?.message}
      />
      <Input
        label="프로젝트 ID (Project ID)"
        placeholder="Project Details에서 확인한 숫자"
        {...register('mixpanel_project_id')}
        error={errors.mixpanel_project_id?.message}
      />

      <div className="space-y-3 pt-3 border-t border-border">
        <p className="text-xs text-on-surface-variant font-medium">
          서비스 계정 (Service Account) 인증
        </p>
        <Input
          label="서비스 계정 사용자명 (Username)"
          placeholder="Service Account username"
          {...register('mixpanel_sa_username')}
          error={errors.mixpanel_sa_username?.message}
        />
        <Input
          label="서비스 계정 시크릿 (Secret)"
          placeholder={shop.mixpanel_sa_secret_set ? '변경 시에만 입력' : 'Service Account secret'}
          type="password"
          {...register('mixpanel_sa_secret')}
          error={errors.mixpanel_sa_secret?.message}
        />
      </div>

      <div className="pt-3 border-t border-border">
        <p className="text-xs text-on-surface-variant font-medium mb-1">
          세션 리플레이
        </p>
        <p className="text-xs text-outline-variant mb-3">
          방문자 화면을 녹화하여 행동을 분석해요. 무료 플랜은 월 1만 건까지.
          방문자 1만 이하라면 100%, 1~5만이면 20~50%를 권장해요.
        </p>
        <Input
          label="녹화 비율 (%)"
          placeholder="0-100"
          type="number"
          {...register('mixpanel_session_replay_percent', {
            valueAsNumber: true,
            min: { value: 0, message: '0 이상이어야 해요.' },
            max: { value: 100, message: '100 이하여야 해요.' },
          })}
          error={errors.mixpanel_session_replay_percent?.message}
        />
      </div>

      <div className="pt-3 border-t border-border">
        <p className="text-xs text-on-surface-variant font-medium mb-1">
          웹 성능 측정
        </p>
        <p className="text-xs text-outline-variant mb-3">
          페이지 로딩 속도(Core Web Vitals)를 측정해 수집해요. 페이지뷰마다
          이벤트가 추가되니 트래픽이 많으면 낮추세요. (0 = 수집 안 함)
        </p>
        <Input
          label="웹 성능 수집 비율 (%)"
          placeholder="0-100"
          type="number"
          {...register('mixpanel_web_vitals_sample_percent', {
            valueAsNumber: true,
            min: { value: 0, message: '0 이상이어야 해요.' },
            max: { value: 100, message: '100 이하여야 해요.' },
          })}
          error={errors.mixpanel_web_vitals_sample_percent?.message}
        />
      </div>
    </div>
  )

  // 광고 계정 연동과 Mixpanel 은 서로 독립적으로 켜고 끌 수 있다.
  // (광고 데이터 자동 수집은 백엔드에서 Mixpanel 토큰 없으면 자동 skip)

  return (
    <>
      <SetupPage
        title="믹스패널 연동"
        connected={isConnected}
        externalLink={externalLink}
        externalLinkLabel="믹스패널 프로젝트"
        prerequisiteMessage={prerequisiteMessage}
        guide={<MixpanelGuide />}
        // 연동 해제 — 공통 틀 ⑤(맨 아래). 총가치 대시보드 카드 아래에 와야 해서 여기서 그린다
        disconnect={
          isConnected && !isEditing
            ? {
                description: '연동을 해제하면 방문자 데이터 수집이 멈춰요.',
                onClick: handleDisconnect,
                loading: saving,
              }
            : undefined
        }
      >
        <SettingsSection
          isEditing={isEditing}
          onEdit={handleEdit}
          onCancel={handleCancel}
          onSave={handleSubmit(onSubmit)}
          saving={saving}
          canSave={isDirty && !blockedByScript}
          canSaveDisabledReason={
            blockedByScript
              ? '카페24 스크립트를 먼저 설치해주세요'
              : '변경된 내용이 없어요'
          }
          viewContent={viewContent}
          editContent={editContent}
          saveLabel="검증 및 저장"
          savingLabel="검증 중…"
        />
        {/* 총가치 대시보드 — 연동된 샵에만 백엔드가 상태를 실어 보낸다 */}
        {isConnected && shop.total_value_board && (
          <TotalValueBoardCard board={shop.total_value_board} />
        )}
      </SetupPage>
      <ConfirmDialog />
    </>
  )
}

// ============================================
// 헬퍼 컴포넌트
// ============================================

/** 인라인 복사 버튼 */
function InlineCopy({ text }: { text: string }) {
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text)
          toast.success('복사되었어요.')
        } catch {
          toast.error('복사에 실패했어요.')
        }
      }}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-surface-low hover:bg-surface-high rounded text-sm font-mono transition-colors"
      title="클릭하여 복사"
    >
      {text}
      <Copy className="h-3 w-3 text-outline-variant" />
    </button>
  )
}

/** 믹스패널 설정 가이드 */
function MixpanelGuide() {
  return (
    <div className="space-y-2">
      <GuideStep number={1} title="믹스패널 가입 및 프로젝트 생성">
        <p>
          <a href="https://mixpanel.com/register" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">mixpanel.com</a>에서
          계정을 만들어요. 이미 계정이 있다면 로그인해주세요.
        </p>
        <TipBox type="warning">
          Data Center는 반드시 <strong>United States</strong>를 선택해야 해요.
        </TipBox>
        <GuideImage src="/images/guide/mixpanel/00-org-datacenter.png" alt="Organization 생성" />
        <p>가입 후 좌측 상단 프로젝트 셀렉터에서 <strong>"Create Project"</strong>를 선택해요.</p>
        <GuideImage src="/images/guide/mixpanel/01-project-selector.png" alt="프로젝트 셀렉터" />
        <p>프로젝트 이름을 입력하고, Timezone을 <strong>Asia/Seoul</strong>로 설정한 후 Create를 클릭해요.</p>
        {/* 프로젝트명 기준은 Data Center 안내와 같은 격으로 띄운다 —
            임의로 지으면 나중에 어느 쇼핑몰 프로젝트인지 대조가 안 된다 */}
        <TipBox type="warning">
          프로젝트 이름은 반드시 <strong>자사몰(브랜드) 이름</strong>으로 입력해야 해요.
          프로젝트 이름이 브랜드와 다르면 나중에 문의하실 때 어느 쇼핑몰인지 확인하는 데 시간이 걸려요.
        </TipBox>
        <GuideImage src="/images/guide/mixpanel/02-create-project.png" alt="프로젝트 생성" />
      </GuideStep>

      <GuideStep number={2} title="Project ID, Project Token 찾기">
        <p>Settings → <strong>Project Settings</strong>에서 상단 <strong>Project Details</strong>의 Project ID를 확인해요.</p>
        <GuideImage src="/images/guide/mixpanel/03-project-id.png" alt="Project ID 위치" />
        <p>같은 페이지 아래 <strong>Access Keys</strong> 섹션에서 <strong>Project Token</strong>을 복사해요.</p>
        <GuideImage src="/images/guide/mixpanel/04-project-token.png" alt="Project Token 위치" />
        <p className="text-xs font-medium text-primary mt-2">→ '프로젝트 토큰', '프로젝트 ID' 입력칸에 넣어 주세요.</p>
      </GuideStep>

      <GuideStep number={3} title="Service Account 생성">
        <p>왼쪽 <strong>Service Accounts</strong> 탭으로 이동하고, <strong>"+ Add Service Account"</strong>를 클릭해요.</p>
        <GuideImage src="/images/guide/mixpanel/05-sa-tab.png" alt="Service Accounts 탭" />
        <p>
          Service Account 이름은 <InlineCopy text="1bpluschool" />으로 선택하고,
          Project Role은 <strong>Admin</strong>, Expires는 <strong>Never</strong>로 설정 후 Add를 클릭해요.
        </p>
        <GuideImage src="/images/guide/mixpanel/06-sa-add.png" alt="Service Account 추가" />
        <p>생성되면 사용자명(Username)과 시크릿(Secret)이 표시돼요.</p>
        <GuideImage src="/images/guide/mixpanel/07-sa-credentials.png" alt="SA credentials" />
        <TipBox type="danger">
          <strong>사용자명과 시크릿은 이 화면에서만 확인 가능해요.</strong> 반드시 둘 다 복사해두세요!
        </TipBox>
        <p className="text-xs font-medium text-primary mt-2">→ '서비스 계정 사용자명', '서비스 계정 시크릿' 입력칸에 넣어 주세요.</p>
      </GuideStep>

      <GuideStep number={4} title="자사몰 사관학교 팀 어드민 초대">
        <p>
          자사몰 사관학교가 대시보드 구성과 분석을 지원할 수 있도록 어드민으로 초대해주세요.
          Settings → Project → <strong>Project Users</strong>에서 우측 상단 <strong>"+ Invite Users"</strong>를 클릭해요.
        </p>
        <GuideImage src="/images/guide/mixpanel/08-invite-users.png" alt="Project Users 페이지에서 Invite Users 클릭" wide />
        <p>
          아래 이메일들을 추가하고 Role을 모두 <strong>Admin</strong>으로 설정한 뒤 초대해주세요.
        </p>
        <ul className="ml-4 space-y-1 text-sm">
          <li>• <InlineCopy text="aaron.koo@inwv.co" /></li>
          <li>• <InlineCopy text="jane.park@inwv.co" /></li>
          <li>• <InlineCopy text="sophia.choi@inwv.co" /></li>
          <li>• <InlineCopy text="smith.kim@inwv.co" /></li>
        </ul>
        <GuideImage src="/images/guide/mixpanel/09-invite-users-modal.png" alt="Invite Users 모달에서 Admin 권한으로 초대" />
      </GuideStep>

      <GuideStep number={5} title="보드 이동 권한 허용">
        <p>
          자사몰 사관학교가 미리 만들어둔 분석 대시보드를 옮겨드리려면 권한이 필요해요.
          Settings → Project → <strong>Moving Boards</strong>로 이동해서 옵션을 <strong>"Allow Across Organizations"</strong>로 변경해주세요.
        </p>
        <GuideImage src="/images/guide/mixpanel/10-moving-boards.png" alt="Moving Boards에서 Allow Across Organizations 선택" wide />
      </GuideStep>

      <StartupProgramInfo />
    </div>
  )
}

/** 스타트업 프로그램 안내 */
function StartupProgramInfo() {
  return (
    <div className="mt-8 pt-6 border-t border-border">
      <h4 className="text-sm font-semibold text-on-surface mb-2">
        💡 Mixpanel 스타트업 프로그램
      </h4>
      <p className="text-sm text-on-surface-variant mb-3">
        스타트업이라면 Mixpanel을 1년간 무료로 사용할 수 있어요.
      </p>
      <ol className="text-sm text-on-surface-variant space-y-1 list-decimal list-inside">
        <li>
          <a href="https://mixpanel.com/startups-apply/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            Mixpanel Startups
          </a>
          에서 <strong>Join Now</strong> 클릭
        </li>
        <li>"How did you hear about us?"에서 <strong>Other: My VC/accelerator is not listed.</strong> 선택</li>
        <li>Referral Partner에 <strong>InsightWave</strong> 입력</li>
      </ol>
    </div>
  )
}
